import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { api, ApiError, setUnauthorizedHandler } from "./api";
import { useProductConfig } from "./productConfig";
import { measure } from "./performance";

/**
 * Session state, held as "who does the API say I am".
 *
 * The cookie is httpOnly, so the client cannot read it and does not try. The
 * only source of truth about the current identity is `GET /v1/me`, which
 * recomputes the role server-side from the allowlist on every call — so a
 * revoked admin's tab demotes itself on its next poll rather than at logout.
 */

export interface Identity {
  email: string;
  role: "admin" | "team";
}

interface SessionState {
  identity: Identity | null;
  status: "loading" | "signed-in" | "signed-out";
  signIn(credential: string): Promise<void>;
  signOut(): Promise<void>;
  retrySession(): void;
  error: string | null;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { config } = useProductConfig();

  /**
   * ANY 401, ANYWHERE, ENDS THE SESSION.
   *
   * The check at mount is not enough on its own: a session lasts twelve hours
   * and expires while the tab is open, so the app went on believing it was
   * signed in while every request came back 401 — which each screen then drew
   * as "could not reach the API", a dead end with no way to recover.
   *
   * Registered before the first request so nothing can slip past it.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setIdentity(null);
      setStatus("signed-out");
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const startedAt = performance.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    api
      .me(controller.signal)
      .then((response) => {
        if (cancelled) return;
        window.clearTimeout(timeout);
        measure("session-discovery", startedAt);
        setIdentity(response.data);
        setStatus("signed-in");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        window.clearTimeout(timeout);
        measure("session-discovery", startedAt);
        // A 401 here is the normal cold-start path, not an error worth showing.
        if (!(cause instanceof ApiError) || cause.status !== 401) {
          setError(
            cause instanceof DOMException && cause.name === "AbortError"
              ? "Analytics took too long to start. You can try again now."
              : `The API is unreachable. It may be starting up.${
                  cause instanceof ApiError && cause.requestId !== undefined
                    ? ` Request ID: ${cause.requestId}.`
                    : ""
                }`,
          );
        }
        setStatus("signed-out");
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt]);

  const retrySession = useCallback(() => {
    setError(null);
    setStatus("loading");
    setAttempt((value) => value + 1);
  }, []);

  const signIn = useCallback(
    async (credential: string) => {
      setError(null);
      try {
        const result = await api.signIn(credential);
        setIdentity({ email: result.email, role: result.role });
        setStatus("signed-in");
      } catch {
        // Deliberately not specific. The server distinguishes "wrong Workspace"
        // from "unverified email" in its logs; telling the browser which one
        // would answer questions for someone who should not be asking.
        setError(
          `That account cannot sign in to ${config.product.name}. Use your @${config.auth.hostedDomain} account.`,
        );
        setStatus("signed-out");
      }
    },
    [config.auth.hostedDomain, config.product.name],
  );

  const signOut = useCallback(async () => {
    await api.signOut();
    setIdentity(null);
    setStatus("signed-out");
  }, []);

  const value = useMemo<SessionState>(
    () => ({ identity, status, signIn, signOut, retrySession, error }),
    [identity, status, signIn, signOut, retrySession, error],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession outside a SessionProvider");
  return value;
}
